import { useEffect, useState } from 'react'
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

import { useGoogleMapsApiKey } from '@/hooks/use-google-maps-api-key'

type GoogleMapsLibrary = 'places' | 'drawing'

/** Superset of libraries used across POS, locations, and address search. */
export const GOOGLE_MAPS_LIBRARIES: GoogleMapsLibrary[] = ['places', 'drawing']

export type GoogleMapsLoaderStatus =
  | 'disabled'
  | 'loading-key'
  | 'missing-key'
  | 'loading-script'
  | 'ready'
  | 'error'

type UseGoogleMapsLoaderOptions = {
  serviceInstanceReference?: string | number | null
  enabled?: boolean
}

let activeApiKey: string | null = null
let activeLibrariesKey = ''
let loadPromise: Promise<void> | null = null

async function ensureGoogleMapsScript(apiKey: string, libraries: GoogleMapsLibrary[]): Promise<void> {
  const librariesKey = libraries.join(',')
  if (loadPromise && activeApiKey === apiKey && activeLibrariesKey === librariesKey) {
    return loadPromise
  }

  activeApiKey = apiKey
  activeLibrariesKey = librariesKey
  setOptions({ key: apiKey, v: 'weekly' })

  loadPromise = Promise.all(libraries.map((library) => importLibrary(library)))
    .then(() => undefined)
    .catch((error) => {
      loadPromise = null
      throw error
    })

  return loadPromise
}

export function resolveGoogleMapsStatusMessage(
  status: GoogleMapsLoaderStatus,
  loadError?: Error | null,
): string {
  switch (status) {
    case 'loading-key':
      return 'Loading Google Maps settings...'
    case 'disabled':
      return 'Map usage is not enabled for this service.'
    case 'missing-key':
      return 'Address search is unavailable. Add a Google Maps API key under organisation credentials or set VITE_GOOGLE_MAPS_API_KEY.'
    case 'loading-script':
      return 'Loading Google Maps address search...'
    case 'error': {
      const message = String(loadError?.message ?? '').trim()
      if (/ApiProjectMapError|InvalidKeyMapError|RefererNotAllowedMapError/i.test(message)) {
        return 'Google Maps could not load. Verify the API key, enable Maps JavaScript API and Places API, and check HTTP referrer restrictions.'
      }
      return message || 'Google Maps failed to load. Enter the address manually.'
    }
    case 'ready':
      return ''
  }
}

export function useGoogleMapsLoader({ serviceInstanceReference, enabled = true }: UseGoogleMapsLoaderOptions = {}) {
  const { mapsApiKey, mapProvider, isLoading: isKeyLoading, source } = useGoogleMapsApiKey({
    serviceInstanceReference,
    enabled,
  })
  const [isLoaded, setIsLoaded] = useState(false)
  const [loadError, setLoadError] = useState<Error | null>(null)

  useEffect(() => {
    if (!enabled) {
      setIsLoaded(false)
      setLoadError(null)
      return
    }

    if (isKeyLoading) {
      return
    }

    if (!mapsApiKey) {
      setIsLoaded(false)
      setLoadError(null)
      return
    }

    let cancelled = false
    setIsLoaded(false)
    setLoadError(null)

    void ensureGoogleMapsScript(mapsApiKey, GOOGLE_MAPS_LIBRARIES)
      .then(() => {
        if (!cancelled) {
          setIsLoaded(true)
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setIsLoaded(false)
          setLoadError(error instanceof Error ? error : new Error(String(error)))
        }
      })

    return () => {
      cancelled = true
    }
  }, [enabled, isKeyLoading, mapsApiKey])

  const status: GoogleMapsLoaderStatus = !enabled
    ? 'disabled'
    : isKeyLoading
    ? 'loading-key'
    : !mapsApiKey
      ? 'missing-key'
      : loadError
        ? 'error'
        : !isLoaded
          ? 'loading-script'
          : 'ready'

  return {
    mapsApiKey,
    mapProvider,
    keySource: source,
    isKeyLoading,
    isLoaded: status === 'ready',
    loadError,
    status,
    statusMessage: resolveGoogleMapsStatusMessage(status, loadError),
    mapsAvailable: status === 'ready',
  }
}
